<script setup lang="ts">
    import { useViewControlsClusterViewModel } from '@/views/3d-planner/view-controls/useViewControlsClusterViewModel';
    import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';

    const props = defineProps<{ planeId: CatstackPlaneId }>();
    const vm = useViewControlsClusterViewModel();

    // Visibility gate stays in the View so a hidden strip lets the wheel
    // pass through to page scroll; advanceSlice itself also no-ops when
    // hidden, but we'd still preventDefault if we let it through here.
    function onWheel(event: WheelEvent) {
        if (!vm.planeStates[props.planeId].visible) return;
        event.preventDefault();
        vm.advanceSlice(props.planeId, event.deltaY);
    }
</script>

<template>
    <div class="ct-slider-strip" @wheel="onWheel">
        <div class="slider-section">
            <v-slider
                :model-value="vm.planeStates[planeId].sliderValue"
                :min="0"
                :max="vm.planeStates[planeId].max"
                :disabled="!vm.planeStates[planeId].visible"
                step="1"
                no-keyboard
                color="primary"
                hide-details
                density="compact"
                thumb-size="12"
                thumb-label
                @update:model-value="(v: number) => vm.setSliderValue(planeId, v)"
            >
                <template #thumb-label>
                    {{ vm.planeStates[planeId].displayIndex }}
                </template>
            </v-slider>
            <span class="label" :class="{ dim: !vm.planeStates[planeId].visible }">
                {{ vm.planeStates[planeId].label }}
            </span>
        </div>
        <v-btn
            class="toggle-btn"
            variant="outlined"
            color="white"
            rounded="pill"
            height="36"
            @click="vm.toggleVisible(planeId)"
        >
            {{ vm.planeStates[planeId].visible ? 'Hide' : 'Show' }}
        </v-btn>
    </div>
</template>

<style scoped lang="scss">
    .ct-slider-strip {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
    }
    .slider-section {
        flex: 1;
        align-self: stretch; // fill the 45px height set by slider-container
        min-width: 0;
        position: relative; // anchor for absolutely positioned slider + label

        // Slider absolutely centred vertically within the strip
        :deep(.v-slider) {
            --v-input-control-height: 12px;
            --v-input-padding-top: 0;
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            transform: translateY(-50%);
            margin: 0;
        }
        :deep(.v-input__control) {
            min-height: unset !important;
            height: 12px !important;
            padding-top: 0 !important;
        }
    }
    .label {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        color: rgb(var(--v-theme-white));
        // Match the 14px Vuetify v-btn default used by the bar buttons —
        // without this the label inherits the default body size (~16px)
        // and reads larger than the surrounding controls.
        font-size: 14px;
        line-height: 1;
        text-align: center;

        &.dim {
            color: rgb(var(--v-theme-secondary));
        }
    }
    // Matches inactive snap buttons (outlined white, weight 400). See
    // SnapToViewButtons.vue for the rationale on the !important overrides.
    .toggle-btn {
        flex: 0 0 auto;
        min-width: 0 !important;
        font-weight: 400 !important;
        letter-spacing: normal !important;
        padding: 0 16px !important;
        text-transform: none !important;
    }
</style>
